// Five booking layouts, from the booking kit ("Openings"): the appointment
// ticket, time in your orbit, the studio agenda, the slot drop and the
// take-a-time poster. Each takes one to four times, so they serve free
// slots (times) and a single free slot (time) alike.
import { flow, textBlock, brand, footer, rule, display, label, body, strong } from '../../kit'
import { dryPainter } from '../../painter'
import { base, ground, detailLine, polyline } from './common'

const one = (env) => env.post.type === 'oneSlot'
const slotsOf = (env) => (one(env) ? [env.post.content.time] : (env.post.content.times || []))
  .map(t => String(t || '').trim()).filter(Boolean).slice(0, 4)
// The small heading (a single slot's small line)
const smallOf = (env) => String((one(env) ? env.post.content.line : env.post.content.eyebrow) || '').trim()
const smallKey = (env) => (one(env) ? 'line' : 'eyebrow')
const timesKey = (env) => (one(env) ? 'time' : 'times')
const more = (env, fr, colour) => (!one(env) && env.post.content.more ? detailLine(env, fr, 'More times available', colour, { slot: 'more' }) : null)

// Circles for one to four times: [x, y, radius] as the kit placed them
const ORBITS = {
  1: [[540, 955, 167]],
  2: [[295, 960, 178], [783, 960, 178]],
  3: [[215, 969, 147], [540, 918, 147], [865, 1000, 147]],
  4: [[207, 901, 137], [531, 855, 137], [864, 947, 137], [519, 1118, 95]],
}

export default {
  // A perforated ticket: the day, then the times, big
  ticket(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const slots = slotsOf(env)
    const x = fr.left - 24
    const tw = w + 48
    const pad = 38
    const inside = (q, top, s) => {
      let y = q.text(env.day, { ...label(env, k.on), size: 34, x: x + pad, y: top, w: tw - pad * 2, slot: 'date' }).bottom
      if (c.service) y = q.text(c.service, { ...body(env, k.on, 32), upper: true, tracking: 0.03, x: x + pad, y: y + 6, w: tw - pad * 2, maxLines: 1, slot: 'service' }).bottom
      const dash = y + 28
      q.line(x + pad, dash, x + tw - pad, dash, k.on, 2, [10, 10])
      q.circle(x, dash, 21, k.paper)
      q.circle(x + tw, dash, 21, k.paper)
      const cols = slots.length === 1 ? 1 : 2
      const size = Math.round((slots.length === 1 ? 116 : 84) * s)
      const step = Math.round(size * 1.7)
      const colW = (tw - pad * 2) / cols
      let bottom = dash + 20
      slots.forEach((t, i) => {
        const ty = dash + Math.round(40 * s) + Math.floor(i / cols) * step
        bottom = q.text(t, { ...strong(env, k.on, size), x: x + pad + (i % cols) * colW, y: ty, w: colW - 20, maxLines: 1, slot: timesKey(env) }).bottom
      })
      return bottom + 8
    }
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(smallOf(env), () => ({ ...label(env, ink), x: fr.left, w }), { gap: 64, slot: smallKey(env) }),
      textBlock(c.headline, s => ({ ...display(env, ink, 146 * s), x: fr.left - 6, w: w + 12, maxLines: 2 }), { gap: 44, slot: 'headline' }),
      {
        gap: 56,
        flex: 1,
        draw: (pp, y, s) => {
          const h = inside(pp.isDry ? pp : dryPainter(pp), y + pad, s) - y + pad
          pp.rect(x, y, tw, h, k.accent)
          inside(pp, y + pad, s)
          return y + h
        },
      },
      more(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // The times in circles round a big message
  orbit(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const slots = slotsOf(env)
    const head = [smallOf(env), env.day].filter(Boolean).join(' / ')
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(head, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 64, slot: smallKey(env) }),
      textBlock(c.headline, s => ({ ...display(env, ink, 150 * s), x: fr.left - 6, w: w + 12, maxLines: 3 }), { gap: 50, slot: 'headline' }),
      textBlock(c.service, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 48, flex: 1, slot: 'service' }),
      slots.length ? {
        gap: c.service ? 28 : 48,
        flex: c.service ? 0 : 1,
        draw: (pp, y, s) => {
          const list = ORBITS[slots.length]
          const top = Math.min(...list.map(([, cy, r]) => cy - r))
          const bottom = Math.max(...list.map(([, cy, r]) => cy + r))
          list.forEach(([cx0, cy0, r0], i) => {
            const cx = fr.W / 2 + (cx0 - fr.W / 2) * s
            const cy = y + (cy0 - top) * s
            const r = r0 * s
            const soft = i % 2 === 1
            pp.circle(cx, cy, r, soft ? k.pop : k.accent)
            const size = Math.max(30, Math.round((r0 < 100 ? 42 : 57) * s))
            pp.text(slots[i], { ...strong(env, soft ? k.ink : k.on, size), x: cx - r + 10, y: cy - Math.round(size * 0.56), w: r * 2 - 20, align: 'center', maxLines: 1, slot: timesKey(env) })
          })
          return y + (bottom - top) * s
        },
      } : null,
      more(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // A daybook: the day down a column of colour, the times in ruled rows
  agenda(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const slots = slotsOf(env)
    const colW = 290
    const rx = fr.left + colW + 40
    flow(p, fr, [
      brand(env, fr, ink),
      rule(fr, ink, { gap: 44 }),
      textBlock(smallOf(env), () => ({ ...label(env, ink), x: fr.left, w }), { gap: 30, slot: smallKey(env) }),
      textBlock(c.headline, s => ({ ...display(env, ink, 112 * s), x: fr.left - 4, w: w + 8, maxLines: 3 }), { gap: 40, slot: 'headline' }),
      {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(490 * s)
          pp.rect(fr.left, y, colW, h, k.accent)
          if (!pp.isDry) {
            const { ctx } = pp
            ctx.save()
            ctx.translate(fr.left + 36, y + h - 40)
            ctx.rotate(-Math.PI / 2)
            pp.text(env.day, { ...display(env, k.on, Math.round(64 * s)), upper: true, x: 0, y: 0, w: h - 80, maxLines: 2, minSize: 34 })
            ctx.restore()
            pp.boxes.push({ slot: 'date', x: fr.left, y, w: colW, h })
          }
          const rowH = h / Math.max(slots.length, 1)
          slots.forEach((t, i) => {
            const ry = y + i * rowH
            pp.line(rx, ry + 1, fr.right, ry + 1, ink, 2)
            pp.text(`0${i + 1}`, { ...body(env, ink, 30), x: rx + 4, y: ry + 20 * s, w: 70, maxLines: 1 })
            pp.text(t, { ...display(env, ink, Math.round(Math.min(78, rowH * 0.5))), minSize: 34, x: rx + 86, y: ry + 18 * s, w: fr.right - rx - 86, maxLines: 1, slot: timesKey(env) })
          })
          pp.line(rx, y + h - 1, fr.right, y + h - 1, ink, 2)
          return y + h
        },
      },
      textBlock(c.service, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 28, slot: 'service' }),
      more(env, fr, ink),
      footer(env, fr, ink),
    ])
  },

  // Loud: the times on tilted cards, one after another
  drop(p, env) {
    const { fr, c, k, w } = base(env)
    const on = ground(p, env, true)
    const slots = slotsOf(env)
    const head = [env.day, c.service].filter(Boolean).join(' / ')
    const cw = 918
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(head, () => ({ ...label(env, on), x: fr.left, w }), { gap: 64, slot: 'date' }),
      textBlock(c.headline, s => ({ ...display(env, on, 104 * s), x: fr.left - 5, w: w + 10, maxLines: 3 }), { gap: 44, slot: 'headline' }),
      slots.length ? {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => {
          const rowH = Math.round((slots.length === 4 ? 133 : Math.min(184, 540 / slots.length)) * s)
          if (!pp.isDry) {
            const { ctx } = pp
            slots.forEach((t, i) => {
              const left = i % 2 ? 112 : 50
              const cx = left + cw / 2
              const cy = y + i * rowH + rowH / 2
              const ch = rowH - Math.round(18 * s)
              ctx.save()
              ctx.translate(cx, cy)
              ctx.rotate(((i % 2 ? 2 : -2) * Math.PI) / 180)
              pp.rect(-cw / 2, -ch / 2, cw, ch, i % 2 ? k.pop : k.paper)
              pp.text(`0${i + 1}`, { ...label(env, k.ink), x: -cw / 2 + 26, y: -ch / 2 + 16, w: 80 })
              const size = Math.round(Math.min(92, rowH * 0.66))
              pp.text(t, { ...strong(env, k.ink, size), x: -cw / 2 + 136, y: -size * 0.58, w: cw - 280, maxLines: 1, minSize: 34 })
              const a = Math.round(Math.min(46, ch * 0.4))
              polyline(pp, [[cw / 2 - 90, a / 2], [cw / 2 - 90 + a, -a / 2]], k.ink, 4)
              polyline(pp, [[cw / 2 - 90 + a * 0.35, -a / 2], [cw / 2 - 90 + a, -a / 2], [cw / 2 - 90 + a, a * 0.15]], k.ink, 4)
              ctx.restore()
              pp.boxes.push({ slot: timesKey(env), x: left + 136, y: cy - ch / 2, w: cw - 280, h: ch })
            })
          }
          return y + rowH * slots.length
        },
      } : null,
      more(env, fr, on),
      footer(env, fr, on),
    ])
  },

  // A noticeboard: a thick rule, a big message, tear-off tabs of times
  notice(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const slots = slotsOf(env)
    const x = fr.left - 24
    const tw = w + 48
    flow(p, fr, [
      brand(env, fr, ink),
      { gap: 44, draw: (pp, y) => { pp.rect(fr.left, y, w, 7, ink); return y + 7 } },
      textBlock(c.headline, s => ({ ...display(env, ink, 128 * s), x: fr.left - 6, w: w + 12, maxLines: 2 }), { gap: 56, slot: 'headline' }),
      textBlock(env.day, () => ({ ...strong(env, ink, 40), upper: true, x: fr.left, w, maxLines: 1 }), { gap: 48, flex: 1, slot: 'date' }),
      textBlock(c.service, () => ({ ...body(env, ink, 32), upper: true, tracking: 0.03, x: fr.left, w, maxLines: 1 }), { gap: 12, slot: 'service' }),
      textBlock(smallOf(env), () => ({ ...label(env, ink), x: fr.left, w }), { gap: 36, slot: smallKey(env) }),
      slots.length ? {
        gap: 40,
        draw: (pp, y, s) => {
          const h = Math.round(260 * s)
          const colW = tw / slots.length
          pp.rect(x, y, tw, h, k.accent)
          pp.line(x, y + 33 * s, x + tw, y + 33 * s, k.on, 2, [9, 9])
          slots.forEach((t, i) => {
            const cx = x + i * colW
            if (i) pp.line(cx, y + 35 * s, cx, y + h, k.on, 2, [9, 9])
            const size = Math.round(Math.min(66, colW * 0.22) * Math.max(s, 0.8))
            // The tabs run past the margins; their words stay inside them
            const tx = Math.max(fr.left, cx + 8)
            const tw2 = Math.min(fr.right, cx + colW - 8) - tx
            pp.text(t, { ...strong(env, k.on, size), minSize: 32, x: tx, y: y + 78 * s, w: tw2, align: 'center', maxLines: 1, slot: timesKey(env) })
            pp.text('Available', { ...label(env, k.on), x: tx, y: y + h - 68 * s, w: tw2, align: 'center' })
          })
          return y + h
        },
      } : null,
      more(env, fr, ink),
      footer(env, fr, ink),
    ])
  },
}

export { slotsOf }
